// Pure magnet-sizing recommendation for the PCB stator generator.
// No Angular / DI dependencies — all math lives here so it can be unit tested.

export type MagnetMode = 'fit'; // future: 'max-torque' | 'low-cogging'
export type MagnetShape = 'round' | 'rect'; // future: 'halbach'

export interface MagnetParams {
  innerRadius: number;
  outerRadius: number;
  innerMargin: number;
  outerMargin: number;
  numPoles: number;
}

export interface MagnetOption {
  shape: MagnetShape;
  /** Raw geometric maximum that fits (un-snapped), mm. May be ≤ 0 for bad geometry. */
  fitsMm: { dia: number } | { length: number; width: number };
  /** Largest standard catalog size ≤ the geometric max, mm. Omitted when nothing fits. */
  standardMm?: { dia: number } | { length: number; width: number };
  /** Standard magnet area ÷ one pole's active annular-sector area, 0–100. 0 when no fit. */
  coveragePct: number;
  /** True when the catalog top-out — not the geometry — is what's limiting this
   *  option: the snapped linear dimension captures less than
   *  CATALOG_CAP_HINT_RATIO of the raw geometric max. False when nothing fits
   *  at all (that's a different, already-warned situation). */
  catalogCapped: boolean;
}

export interface MagnetRecommendation {
  count: number;             // = numPoles
  meanRadiusMm: number;      // placement radius (centre of magnet)
  radialCoverageMm: number;  // coilOuter − coilInner
  poleArcMm: number;         // pole-pitch arc at mean radius (pre-gap)
  gapMm: number;             // inter-magnet gap applied
  options: MagnetOption[];   // [round, rect]
  warning?: string;          // set when geometry can't fit sensible magnets
}

// Buyable standard neodymium stock, mm — every entry here is a size actually
// sold off-the-shelf (disc and/or block) by at least one real vendor, not a
// mathematically-convenient round number. Sizes ≤ 30 mm are the original
// catalog (unchanged). Sizes > 30 mm added 2026-07-26 to stop capping the
// recommendation on large boards (`fitsMm` kept growing past 30 while
// `standardMm` stalled) — each verified against a live listing:
//   35 mm disc — supermagnete.de "Disc magnet 35 x 20 mm" (S-35-20-N)
//     https://www.supermagnete.de/eng/disc-magnets-neodymium/disc-magnet-35mm-20mm_S-35-20-N
//   40 mm disc — first4magnets "N42 Neodymium Disc Magnet — 40mm dia x 5mm thick"
//     https://www.first4magnets.com/circular-disc-rod-c34/40mm-dia-x-5mm-thick-n42-neodymium-magnet-16-3kg-pull-p3252
//   50 mm disc — first4magnets "50mm dia x 10mm N52 Neodymium Magnet — 45kg Pull"
//     https://www.first4magnets.com/product/50mm-dia-x-10mm-thick-ultra-high-performance-n52-neodymium-magnet-42kg-pull-19214
//   60 mm disc — supermagnete.de "Disc magnet 60 x 5 mm" (S-60-05-N)
//     https://www.supermagnete.de/eng/disc-magnets-neodymium/disc-magnet-60mm-5mm_S-60-05-N
// (Block stock exists too at these scales, e.g. supermagnete's 40×40×20 mm
// block Q-40-40-20-N, confirming the sizes aren't disc-only curiosities.)
export const STANDARD_MAGNET_SIZES_MM = [
  2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 35, 40, 50, 60,
] as const;

/** Below this ratio of (snapped standard size ÷ raw geometric max), the
 *  catalog top-out — not the geometry — is what's capping the recommendation:
 *  the board could carry a magnet that fills the sector, but the largest
 *  stocked size gives up more than 25% of the available linear dimension.
 *  Used to surface a UI hint pointing at a custom-size override or a
 *  multi-magnet-per-pole layout instead. */
export const CATALOG_CAP_HINT_RATIO = 0.75;

function isCatalogCapped(stdLinear: number | undefined, fitsLinear: number): boolean {
  if (stdLinear === undefined || fitsLinear <= 0) return false;
  return stdLinear < CATALOG_CAP_HINT_RATIO * fitsLinear;
}

/** Largest catalog value ≤ value, or null if value is below the smallest entry. */
export function snapDown(
  value: number,
  catalog: readonly number[] = STANDARD_MAGNET_SIZES_MM,
): number | null {
  let best: number | null = null;
  for (const s of catalog) {
    if (s <= value && (best === null || s > best)) best = s;
  }
  return best;
}

export function computeMagnetRecommendation(
  p: MagnetParams,
  opts: { mode?: MagnetMode; gapFraction?: number } = {},
): MagnetRecommendation {
  const gapFraction = opts.gapFraction ?? 0.10;

  const coilInner = p.innerRadius + p.innerMargin;
  const coilOuter = p.outerRadius - p.outerMargin;
  const radialCoverage = coilOuter - coilInner;
  const rMean = (coilInner + coilOuter) / 2;
  const poleArc = (2 * Math.PI * rMean) / p.numPoles;
  const gapMm = poleArc * gapFraction;
  const tangentialRoom = poleArc - gapMm;
  const poleSectorArea = (Math.PI * (coilOuter * coilOuter - coilInner * coilInner)) / p.numPoles;

  const base: MagnetRecommendation = {
    count: p.numPoles,
    meanRadiusMm: rMean,
    radialCoverageMm: radialCoverage,
    poleArcMm: poleArc,
    gapMm,
    options: [],
  };

  if (p.numPoles < 2) {
    return { ...base, warning: 'Need at least 2 magnet poles.' };
  }
  if (radialCoverage <= 0) {
    return { ...base, warning: 'No radial room — increase outer radius or reduce margins.' };
  }
  const smallest = STANDARD_MAGNET_SIZES_MM[0];
  if (tangentialRoom < smallest) {
    return {
      ...base,
      warning: `Too many poles for this radius; magnets would be smaller than ${smallest} mm (overlap).`,
    };
  }

  // Round: bounded by the smaller of radial coverage and tangential room.
  const maxDia = Math.min(radialCoverage, tangentialRoom);
  const stdDia = snapDown(maxDia);
  const roundArea = stdDia !== null ? Math.PI * (stdDia / 2) * (stdDia / 2) : 0;
  const round: MagnetOption = {
    shape: 'round',
    fitsMm: { dia: maxDia },
    standardMm: stdDia !== null ? { dia: stdDia } : undefined,
    coveragePct: poleSectorArea > 0 ? (roundArea / poleSectorArea) * 100 : 0,
    catalogCapped: isCatalogCapped(stdDia ?? undefined, maxDia),
  };

  // Rectangle: length along radial, width along tangential. Straight bars bind
  // at their inner corners (radius rMean − length/2), not at rMean, so width
  // and length must be co-solved rather than each snapped independently —
  // see the AMENDED "Rectangle block" section of the 2026-06-27 design doc.
  const halfAngle = (Math.PI / p.numPoles) * (1 - gapFraction);
  const maxWidthAt = (l: number) => {
    const innerCornerR = rMean - l / 2;
    return Math.min(tangentialRoom, 2 * innerCornerR * Math.tan(halfAngle));
  };

  let bestLength: number | null = null;
  let bestWidth: number | null = null;
  let bestArea = -Infinity;
  for (let i = STANDARD_MAGNET_SIZES_MM.length - 1; i >= 0; i--) {
    const l = STANDARD_MAGNET_SIZES_MM[i];
    if (l > radialCoverage) continue;
    const innerCornerR = rMean - l / 2;
    if (innerCornerR <= 0) continue;
    const w = snapDown(maxWidthAt(l));
    if (w === null) continue;
    const area = l * w;
    if (area > bestArea) {
      bestArea = area;
      bestLength = l;
      bestWidth = w;
    }
  }

  const stdLength = bestLength;
  const stdWidth = bestWidth;
  const rectArea = stdLength !== null && stdWidth !== null ? stdLength * stdWidth : 0;
  const rect: MagnetOption = {
    shape: 'rect',
    fitsMm: stdLength !== null
      ? { length: radialCoverage, width: maxWidthAt(stdLength) }
      : { length: radialCoverage, width: tangentialRoom },
    standardMm: stdLength !== null && stdWidth !== null ? { length: stdLength, width: stdWidth } : undefined,
    coveragePct: poleSectorArea > 0 ? (rectArea / poleSectorArea) * 100 : 0,
    // Radial (length) ratio, not tangential — the radial dimension is what
    // the user notices when the sector isn't filled (see CATALOG_CAP_HINT_RATIO).
    catalogCapped: isCatalogCapped(stdLength ?? undefined, radialCoverage),
  };

  return { ...base, options: [round, rect] };
}
